import {
  ButtonGroup,
  closeCurrentDialog,
  getFilePreviewUrl,
  showNotification,
} from 'platform/components';
import {Box, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useMemo, useState} from 'react';

// used for datagrid data mapping, so its fine
// eslint-disable-next-line no-restricted-imports
import {isNil, isNotNil, mergeAll, path} from 'ramda';

import {ContextTarget} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, openFile} from 'shared/web';

import type {DataGridBehaviorOverrides} from 'features/datagrid';
import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {downloadFile} from '../../utils/downloadFile';
import {DmsDataGrid} from '../DmsDataGrid/DmsDataGrid';
import {UploadedCustomerMessageFile} from '../SendCustomerEmailForm/types/UploadedCustomerMessageFile';

const PREDEFINED_COLUMNS = ['fileName'] as const;
type FileNameColumnData = {
  remoteUrl: string;
  id: string;
  contentType: string;
  title: string;
};

interface EntityDocumentsSelectGridProps {
  contextTarget: ContextTarget;
  recordId: string;
  onSelect: (files: UploadedCustomerMessageFile[]) => void;
  vehicleId?: string | Nullish;

  /**
   * @deprecated used only in checklists as a temporary solution. DO NOT USE
   **/
  isSingleSelect?: boolean;
}

export function EntityDocumentsSelectGrid(props: EntityDocumentsSelectGridProps) {
  const [selectedFiles, setSelectedFiles] = useState<UploadedCustomerMessageFile[]>([]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
  }) => {
    const file = getColumnData<FileNameColumnData>('fileName')?.value;

    match(actionKey)
      .with('preview', () => {
        if (isNil(file?.remoteUrl)) {
          showNotification.error();
          return;
        }

        const previewUrl = getFilePreviewUrl(file.remoteUrl, file?.contentType);

        openFile(previewUrl);
      })
      .with('download', () => {
        if (isNil(file?.remoteUrl)) {
          showNotification.error();
          return;
        }

        downloadFile(file.remoteUrl, {fileName: file.title, fileType: 'attachment'});
      });
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => {
      const entityIds = [props.recordId, props.vehicleId].filter(isNotNil);
      const entityTypes = [
        props.contextTarget,
        isNotNil(props.vehicleId) ? 'vehicle' : null,
      ].filter(isNotNil);

      return mergeAll([
        filter,
        {
          targetEntityId: entityIds.join('|'),
          targetEntity: entityTypes.join('|'),
        },
      ]);
    },
    [props.recordId, props.contextTarget, props.vehicleId]
  );

  const definitionBehaviorOverrides = useMemo<DataGridBehaviorOverrides | undefined>(() => {
    if (props.isSingleSelect) {
      return {
        rowSelectMode: 'SINGLE',
      };
    }

    return undefined;
  }, [props.isSingleSelect]);

  return (
    <VStack spacing={4}>
      <Box>
        <DmsDataGrid
          gridCode="document-context-list-with-selection"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          _useAsLastResort_definitionBehaviorOverrides={definitionBehaviorOverrides}
          onRowSelectionChange={(data) => {
            const files = data.map(path<FileNameColumnData>(['fileName', 'value'])).filter(Boolean);
            setSelectedFiles(
              files.map((item) => ({
                fileId: item.id,
                fileName: item.title,
                fileType: item.contentType,
                fileUrl: item.remoteUrl,
                shouldConvertToPdf: false,
              }))
            );
          }}
          queryModifier={queryModifier}
          autoHeight
        />
      </Box>
      <ButtonGroup
        align="right"
        buttons={[
          {
            title: i18n.t('general.actions.cancel'),
            variant: 'secondary',
            onClick: closeCurrentDialog,
          },

          {
            title: i18n.t('general.labels.select'),
            onClick: () => {
              props.onSelect(selectedFiles);
              closeCurrentDialog();
            },
          },
        ]}
      />
    </VStack>
  );
}
